import React, { useRef, useEffect } from 'react'
import Icons from '../components/Icons'
import Reveal, { Zoom, Fade } from 'react-awesome-reveal'
import { keyframes } from '@emotion/react'
import { useTranslation } from 'gatsby-plugin-react-i18next'

const AboutSection = ({ onSectionChange, currentSection }) => {
  const aboutSectionRef = useRef()

  const handleScroll = (event) => {
    const scrollTop = aboutSectionRef.current.getBoundingClientRect().bottom
    if (scrollTop <= 100 && currentSection === 'section2') {
      onSectionChange('section3')
    } else if (scrollTop > 100 && currentSection === 'section3') {
      onSectionChange('section2')
    }
  }

  const { t } = useTranslation()

  useEffect(() => {
    if (currentSection === 'section2') {
      animateValues()
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  })

  const skills = [
    {
      name: t('aboutSection.skills.fe'),
      value: 90,
      animation: keyframes`
      from {
        width: 0%;
      }
    
      to {
        width: 90%;
      }
    `,
      color: 'bg-yellow-500'
    },
    {
      name: t('aboutSection.skills.be'),
      value: 80,
      animation: keyframes`
      from {
        width: 0%;
      }
    
      to {
        width: 80%;
      }
    `,
      color: 'bg-radical-red'
    },
    {
      name: t('aboutSection.skills.devops'),
      value: 75,
      animation: keyframes`
      from {
        width: 0%;
      }
    
      to {
        width: 75%;
      }
    `,
      color: 'bg-royal-blue'
    },
    {
      name: t('aboutSection.skills.leadership'),
      value: 80,
      animation: keyframes`
      from {
        width: 0%;
      }
    
      to {
        width: 80%;
      }
    `,
      color: 'bg-turquoise'
    }
  ]

  const stats = [
    {
      name: t('aboutSection.stats.xp'),
      value: 10,
      prefix: '+',
      suffix: '',
      duration: 3500,
      icon: 'peak'
    },
    {
      name: t('aboutSection.stats.coffee'),
      value: 7350,
      prefix: '+',
      suffix: '',
      duration: 1000,
      icon: 'coffee-cup'
    },
    {
      name: t('aboutSection.stats.clients'),
      value: 100,
      prefix: '',
      suffix: '%',
      duration: 3000,
      icon: 'customer'
    }
  ]

  const social = [
    {
      name: 'twitter',
      link: 'https://twitter.com/alexlionco'
    },
    {
      name: 'github',
      link: 'https://github.com/alexandre-lion'
    },
    {
      name: 'linkedin',
      link: 'https://www.linkedin.com/in/alexlion/'
    },
    {
      name: 'medium',
      link: 'https://alexandre-lion.medium.com/'
    },
    {
      name: 'unsplash',
      link: 'https://unsplash.com/@alexandrelion'
    }
  ]

  const timers = {}

  const animateValues = () => {
    stats.forEach(el => {
      const end = el.value
      const start = 0
      const duration = el.duration
      const range = end - start
      let current = start
      const m = end > 1000 ? 10 : 1
      const increment = end > start ? 1 * m : -1 * m
      const stepTime = Math.abs(Math.floor(duration / range))
      const obj = document.getElementById(el.icon)
      if (timers[el.icon]) clearInterval(timers[el.icon])
      timers[el.icon] = setInterval(() => {
        current += increment
        obj.innerHTML = current
        if (current === end) {
          clearInterval(timers[el.icon])
        }
      }, stepTime)
    })
  }

  return (
    <section
      ref={aboutSectionRef}
      id="section2"
      className="relative bg-gray-100 w-full"
    >
      <div className="w-5/6 md:w-3/4 lg:w-4/6 mx-auto py-16">
      <Fade direction="up">
        <h2 className="title font-bold text-4xl text-martinique relative z-10">
          {t('aboutSection.title')}
        </h2>
      </Fade>

        <div className="flex flex-col md:flex-row py-12 md:py-24 justify-between items-start content-center">
          <div className="mx-auto">
            <Zoom className="mx-auto w-52 rounded-full">
              <img width="208" height="208" className="rounded-full border-4 border-white"
                src={'/img/avatar.png'} alt="Alexandre Lion"
              />
            </Zoom>
            <div id="socials" className="flex justify-center my-5 py-5 relative z-10">
              {social.map((network, index) => {
                return (
                  <a key={index} className="mr-4" href={network.link}>
                    <Icons name={network.name} className="text-martinique hover:text-yellow-400 fill-current transition delay-300" />
                  </a>
                )
              })}
            </div>
          </div>

          <div className="flex flex-col md:flex-row w-full md:w-2/3 lg:w-4/5 mt-12 md:mt-0 md:ml-12 justify-start bg-white rounded-xl shadow-xl p-5 md:p-10 text-martinique relative arrow">
            <div className="md:mr-6 md:w-3/6 lg:w-3/5 font-light lg:leading-9 text-martinique-400">
            <p dangerouslySetInnerHTML={{
              __html: t('aboutSection.bio.first')
            }}></p>
              <p dangerouslySetInnerHTML={{
                __html: t('aboutSection.bio.second')
              }}>
              </p>
              <p dangerouslySetInnerHTML={{
                __html: t('aboutSection.bio.third')
              }}>
              </p>
              <p dangerouslySetInnerHTML={{
                __html: t('aboutSection.bio.fourth')
              }}>
              </p>
            </div>
            <div className="mt-10 md:m-0 flex-1">
              {skills.map((value, index) => {
                return (
                  <div key={index}>
                    <div
                      className={`flex justify-between items-center ${
                        index > 0 ? 'mt-3' : ''
                      }`}
                    >
                      <span className="font-medium">{value.name}</span>
                      <span className="text-gray-500 text-xs">
                        { value.value }%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full relative mt-2">
                      <Reveal keyframes={value.animation} delay={index * 300} className={`${value.color} h-2 absolute rounded-full`}>
                        <div>
                        </div>
                      </Reveal>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row w-2/3 md:w-full mx-auto items-start content-start md:content-center md:items-center justify-center md:justify-between">
          {stats.map((value, index) => {
            return (
              <div
                className={`flex ${index > 0 ? 'mt-8 md:mt-0' : ''} mt-0`}
                key={index}
              >
                <Icons
                  name={value.icon}
                  className="text-athens-gray-600 fill-current w-24 h-12"
                />
                <div className="flex flex-col">
                  <span className="font-bold text-4xl text-martinique-500">
                    {value.prefix}<span id={value.icon}>0</span>{value.suffix}
                  </span>
                  <span className="text-athens-gray-800 font-light">
                    {value.name}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default AboutSection
